import {
  deleteConversation,
  fetchConversations,
  renameConversation,
  type Conversation,
  type ConversationsResponse,
} from '@/services/conversationService';
import { CACHE_PREFIXES, PaginatedCache } from '@/utils/cache';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

export const useConversationStore = defineStore('conversations', () => {
  const conversations = ref<Conversation[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const selectedConversation = ref<Conversation | null>(null);

  // Pagination state
  const total = ref(0);
  const limit = ref(10);
  const offset = ref(0);
  const hasMore = ref(false);

  // Agent filter state
  const activeAgentFilter = ref<string | null>(null);

  // Cache instance with 5-minute TTL and localStorage persistence
  const conversationCache = new PaginatedCache<Conversation>({
    ttl: 5 * 60 * 1000,
    persist: true,
    storageKeyPrefix: CACHE_PREFIXES.CONVERSATIONS,
  });

  const loadConversations = async (newLimit = 10, newOffset = 0) => {
    const cacheKey = conversationCache.generateKey({
      limit: newLimit,
      offset: newOffset,
      ...(activeAgentFilter.value ? { agentId: activeAgentFilter.value } : {}),
    });

    // Check cache first
    const cachedEntry = await conversationCache.get(cacheKey);
    if (cachedEntry) {
      // Reconstruct state from cache
      if (newOffset === 0) {
        conversations.value = cachedEntry.data;
      } else {
        conversations.value = [...conversations.value, ...cachedEntry.data];
      }

      total.value = cachedEntry.total;
      hasMore.value = cachedEntry.hasMore;
      offset.value = newOffset;
      limit.value = newLimit;

      console.log('Conversations loaded from cache:', conversations.value);
      return;
    }

    // Cache miss - fetch from API
    try {
      isLoading.value = true;
      error.value = null;

      const response: ConversationsResponse = await fetchConversations(
        newLimit,
        newOffset,
        activeAgentFilter.value,
      );

      // Store in cache
      conversationCache.set(cacheKey, {
        data: response.data,
        total: response.total,
        hasMore: response.has_more,
        timestamp: Date.now(),
      });

      // If offset is 0, replace conversations; otherwise append
      if (newOffset === 0) {
        conversations.value = response.data;
      } else {
        conversations.value = [...conversations.value, ...response.data];
      }

      total.value = response.total;
      limit.value = response.limit;
      offset.value = response.offset;
      hasMore.value = response.has_more;

      console.log(
        'Conversations loaded from API and cached:',
        conversations.value,
      );
    } catch (err) {
      console.error('Failed to load conversations:', err);
      error.value = 'Failed to load conversations';
      conversations.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const loadMoreConversations = async () => {
    if (!hasMore.value || isLoading.value) {
      return;
    }

    const newOffset = offset.value + limit.value;
    await loadConversations(limit.value, newOffset);
  };

  const invalidateCache = () => {
    conversationCache.invalidateAll();
    console.log('Conversation cache invalidated');
  };

  const refreshConversations = async () => {
    invalidateCache();
    await loadConversations(limit.value, 0);
  };

  const getConversationById = (id: string): Conversation | undefined => {
    return conversations.value.find((c) => c.id === id);
  };

  const setSelectedConversation = (conversation: Conversation | null) => {
    selectedConversation.value = conversation;
  };

  const getSelectedConversation = (): Conversation | null => {
    return selectedConversation.value;
  };

  const clearConversations = () => {
    conversations.value = [];
    total.value = 0;
    offset.value = 0;
    hasMore.value = false;
    error.value = null;
  };

  const removeConversation = (conversationId: string) => {
    conversations.value = conversations.value.filter(
      (c) => c.id !== conversationId,
    );
    total.value = Math.max(0, total.value - 1);
  };

  const addConversation = (conversation: Conversation) => {
    // Check if conversation already exists
    const exists = conversations.value.some((c) => c.id === conversation.id);
    if (!exists) {
      // Prepend to the beginning (most recent first)
      conversations.value.unshift(conversation);
      total.value += 1;
    }
  };

  const updateConversationTitle = (
    conversationId: string,
    newTitle: string,
  ): void => {
    const conversation = conversations.value.find(
      (c) => c.id === conversationId,
    );
    if (conversation) {
      conversation.title = newTitle;
    }

    // Also update selected conversation if it matches
    if (selectedConversation.value?.id === conversationId) {
      selectedConversation.value.title = newTitle;
    }
  };

  const renameConversationById = async (
    conversationId: string,
    title: string,
  ): Promise<void> => {
    try {
      error.value = null;
      await renameConversation(conversationId, title);
      updateConversationTitle(conversationId, title);
      invalidateCache();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to rename conversation';
      error.value = errorMessage;
      console.error('Failed to rename conversation:', err);
      throw err;
    }
  };

  const deleteConversationById = async (
    conversationId: string,
  ): Promise<void> => {
    try {
      error.value = null;
      await deleteConversation(conversationId);
      removeConversation(conversationId);
      invalidateCache();
      console.log(`Conversation ${conversationId} deleted successfully`);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to delete conversation';
      error.value = errorMessage;
      console.error('Failed to delete conversation:', err);
      throw err;
    }
  };

  const setAgentFilter = async (agentId: string | null) => {
    activeAgentFilter.value = agentId;
    invalidateCache();
    await loadConversations(limit.value, 0);
  };

  const waitUntilReady = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      if (!isLoading.value) {
        resolve();
      } else {
        const unwatch = watch(isLoading, (newVal) => {
          if (!newVal) {
            unwatch();
            resolve();
          }
        });
      }
    });
  };

  return {
    conversations,
    isLoading,
    error,
    selectedConversation,
    total,
    limit,
    offset,
    hasMore,
    activeAgentFilter,
    conversationCache,
    loadConversations,
    loadMoreConversations,
    refreshConversations,
    invalidateCache,
    getConversationById,
    setSelectedConversation,
    getSelectedConversation,
    clearConversations,
    removeConversation,
    addConversation,
    updateConversationTitle,
    renameConversationById,
    deleteConversationById,
    setAgentFilter,
    waitUntilReady,
  };
});
